

import Vue from 'vue'
import Component from "vue-class-component";
import {namespace} from "vuex-class";
import {IOnOffHandlers} from "bonomo/dist";
import {IShortServiceProvider} from "@/model/shortInterfaces";
import ServiceProviderChip from "@/components/shared/chips/ServiceProviderChip.vue";
import {Prop, Watch} from "vue-property-decorator";
import {BetterDataTableHeader} from "@/model/betterVuetifyInterfaces";
import {ICategoryServiceProvider} from "@/store/modules/serviceProviderModule";
import {IChipStyle} from "@/model/chipStyleInterfaces";
import {EServiceProviderType} from "@/model/Service/serviceProviderInterfaces";
import ServiceProviderTypeChip from "@/components/shared/chips/ServiceProviderTypeChip.vue";

const serviceProviderModule = namespace("serviceProviderModule");

  interface IProviderWithConnect extends IShortServiceProvider {
    connectModel: boolean;
  }
  interface ICategoryServiceProviderWithConnect extends ICategoryServiceProvider {
    connectModel: boolean;
  }

  @Component({
    components: {ServiceProviderTypeChip, ServiceProviderChip}
  })
  export default class ServiceProviderCategories extends Vue {

    @serviceProviderModule.Getter('serviceProviders')
    private providers!: IShortServiceProvider[];
    @serviceProviderModule.Getter('categoryServiceProviders')
    private categoryServiceProviders!: ICategoryServiceProvider[];

    @Prop()
    protected categoryId!: number;

    @Watch('categoryId')
    onUrlChanged() {
      this.loadCategoryProviders();
    }

    protected providerItems: IProviderWithConnect[] = [];
    protected categoryProviderItems: ICategoryServiceProviderWithConnect[] = [];
    protected search: string = '';
    protected onlyAssigned: number = 1;
    private connectionsLoading: boolean = true;
    private subscribes: IOnOffHandlers = {};

    protected get headers(): BetterDataTableHeader[] {
      return [
        { text: this.$t('common.id'), align: 'start', value: 'id' },
        { text: this.$t('common.name'), align: 'start', value: 'name' },
        { text: this.$t('common.type'), align: 'center', value: 'type' },
        { text: this.$t('common.active'), align: 'center', value: 'active', sortable: false },
        { text: this.$t('common.assigned'), align: 'center', value: 'connectModel', sortable: false },
      ];
    }

    protected get headersForSort(): BetterDataTableHeader[] {
      return [
        { text: this.$t('common.order'), align: 'start', value: 'sp_position' },
        { text: this.$t('common.id'), align: 'start', value: 'id' },
        { text: this.$t('common.name'), align: 'start', value: 'name' },
        { text: this.$t('common.type'), align: 'center', value: 'service_provider_type' },
        { text: this.$t('common.assigned'), align: 'center', value: 'connectModel', sortable: false },
      ];
    }

    protected get notDeletedProviderItems(): any[] {
      return this.providerItems.filter(
          (each: IShortServiceProvider) => !each.deleted_at
      );
    }

    get categoryItems() {
      const categories = this.$t('services.provider.categories.values');
      return Object.keys(categories).map(
          (categoryId) => ({
            text: (categories as any)[categoryId],
            value: '' + categoryId,
          })
      );
    }

    get showMovers(): boolean {
      return true;
    }

    created() {
      this.loadData();
      this.loadCategoryProviders();
      this.subscribes = this.$ons({
        'login.OK': () => {
          this.loadData();
          this.loadCategoryProviders();
        },
        'backend.call.OK.serviceProvidersLoaded': this.onProvidersLoaded,
        'backend.call.KO.serviceProvidersLoaded': this.onKO,
        'backend.call.OK.serviceProviderLoaded': this.loadCategoryProviders,
        'backend.call.KO.serviceProviderLoaded': this.onKO,
        'backend.call.OK.categoryServiceProvidersLoaded': this.onCategoryProvidersLoaded,
        'backend.call.KO.categoryServiceProvidersLoaded': this.onKO,
      });
    }

    beforeDestroy() {
      this.$offs(this.subscribes);
    }

    protected getServiceProvider(spId: number) {
      return this.providers
          ? this.providers.find((each: IShortServiceProvider) => each.id == spId)
          : null;
    }

    protected serviceProviderChipStyle(spType: EServiceProviderType): IChipStyle {
      return ServiceProviderChip.chipStyleOf(spType);
    }

    protected naturalPosition(item: ICategoryServiceProviderWithConnect): number {
      return this.categoryProviderItems.indexOf(item) + 1 ;
    }

    protected isFirst(item: ICategoryServiceProviderWithConnect): boolean {
      return this.naturalPosition(item) == 1;
    }

    protected isLast(item: ICategoryServiceProviderWithConnect): boolean {
      return this.naturalPosition(item) == this.categoryProviderItems.length;
    }

    protected onMoveUp(item: ICategoryServiceProviderWithConnect) {
      this.connectionsLoading = true;
      this.$store.dispatch('serviceProviderModule/moveUp', {categoryId: this.categoryId, serviceProviderId: item.service_provider_id});
    }

    protected onMoveDown(item: ICategoryServiceProviderWithConnect) {
      this.connectionsLoading = true;
      this.$store.dispatch('serviceProviderModule/moveDown', {categoryId: this.categoryId, serviceProviderId: item.service_provider_id});
    }

    private loadData() {
      this.$store.dispatch('serviceProviderModule/fetchServiceProviders');
    }

    private loadCategoryProviders() {
      if (this.categoryId) {
        this.$store.dispatch('serviceProviderModule/fetchCategoryServiceProviders', this.categoryId);
      }
    }

    private onCategorySelected(categoryId: number) {
      if (this.categoryId != categoryId) {
        this.connectionsLoading = true;
        this.$router.push({
          name: 'serviceProviderCategories',
          params: { categoryId: '' + categoryId },
        });
      }
    }

    protected assignedProvidersFilter(
      value: any,
      search: string,
      item: ICategoryServiceProviderWithConnect
    ) {
      const provider = this.getServiceProvider(item.service_provider_id) || {} as any;
      const notBySearch = this.search.length &&
        (provider.name||'').toString().toLowerCase().indexOf(this.search.toLowerCase()) === -1
      return !notBySearch && !provider.deleted_at;
    }

    private onProvidersLoaded() {
      this.providerItems = this.providers.map(
        each => Object.assign({}, each, { connectModel: false })
      );
      this.makeProviderItems();
    }

    private onCategoryProvidersLoaded() {
      this.categoryProviderItems = this.categoryServiceProviders.map(
        each => Object.assign({}, each, {
            connectModel: false,
          })
      );
      // there shouldn't be an equal anyway, and if there is, we still have to order them, anyway, anyway.
      this.categoryProviderItems.sort((a, b) => a.sp_position > b.sp_position ? 1 : -1);
      this.makeProviderItems();
    }

    private makeProviderItems() {
      this.providerItems.forEach(
        (each) => each.connectModel = this.hasProvider(each.id)
      );
      this.categoryProviderItems.forEach(
        (each) => each.connectModel = true
      );
      this.connectionsLoading = false;
    }

    private hasProvider(providerId: number) {
      return this.categoryServiceProviders &&
          !!this.categoryServiceProviders.find((each: any) => each.service_provider_id == providerId);
    }

    private onSwitch(serviceProviderId: number, connect: boolean) {
      this.connectionsLoading = true;
      this.$store.dispatch('serviceProviderModule/connectCategory', {
        serviceProviderId,
        categoryId: this.categoryId,
        connect: connect,
      });
    }

    private onKO() {
      this.connectionsLoading = false;
    }

  }
